import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  HStack,
  Badge,
  Divider,
  Box,
  useColorModeValue,
  Spinner,
  Flex,
  SimpleGrid,
} from '@chakra-ui/react';

const PrecatoriosSelecionadasModal = ({ isOpen, onClose, precatorio, loading }) => {
  const bg = useColorModeValue('gray.50', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const valueColor = useColorModeValue('gray.800', 'gray.100');

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const InfoRow = ({ label, value, isImportant = false }) => (
    <Box>
      <Text fontSize="sm" color={labelColor} fontWeight="medium">
        {label}
      </Text>
      <Text 
        fontSize={isImportant ? "lg" : "md"} 
        color={valueColor}
        fontWeight={isImportant ? "bold" : "normal"}
        mt={1}
      >
        {value || '-'}
      </Text>
    </Box>
  );

  const getStatusColor = (status) => {
    const colors = {
      'Não Contatou': 'gray',
      'Pendente': 'yellow',
      'Em Análise': 'blue',
      'Proposta Enviada': 'purple',
      'Proposta Aceita': 'green',
      'Contrato Assinado': 'teal',
      'Pago': 'green',
      'Arquivado': 'red'
    };
    return colors[status] || 'gray';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Detalhes do Precatório
          {precatorio && (
            <Badge ml={3} colorScheme={getStatusColor(precatorio.status)}>
              {precatorio.status}
            </Badge>
          )}
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          {loading ? (
            <Flex justify="center" align="center" h="200px">
              <Spinner size="lg" />
            </Flex>
          ) : precatorio ? (
            <VStack spacing={6} align="stretch">
              {/* Main Info */}
              <Box bg={bg} p={4} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                <Text fontSize="lg" fontWeight="bold" mb={3}>
                  Informações Principais
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <InfoRow label="Número" value={precatorio.numero_precatorio} isImportant />
                  <InfoRow label="Valor Atualizado" value={formatCurrency(precatorio.valor_atualizado)} isImportant />
                  <InfoRow label="Ordem" value={precatorio.ordem} />
                  <InfoRow label="Orçamento" value={precatorio.orcamento} />
                  <InfoRow label="Natureza" value={precatorio.natureza} />
                  <InfoRow label="Prioridade" value={precatorio.prioridade || 'Normal'} />
                </SimpleGrid>
              </Box>

              {/* Parties Info */}
              <Box bg={bg} p={4} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                <Text fontSize="lg" fontWeight="bold" mb={3}>
                  Partes Envolvidas
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <InfoRow label="Ente" value={precatorio.ente} />
                  <InfoRow label="Beneficiário" value={precatorio.beneficiario} />
                  <InfoRow label="CPF/CNPJ" value={precatorio.cpf_cnpj} />
                  <InfoRow label="Requisitante" value={precatorio.requisitante} />
                </SimpleGrid>
              </Box>

              {/* Process Info */}
              <Box bg={bg} p={4} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                <Text fontSize="lg" fontWeight="bold" mb={3}>
                  Informações do Processo
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <InfoRow label="Processo de Origem" value={precatorio.processo_origem} />
                  <InfoRow label="Vara" value={precatorio.vara} />
                  <InfoRow label="Comarca" value={precatorio.comarca} />
                  <InfoRow label="Natureza do Crédito" value={precatorio.natureza_credito} />
                </SimpleGrid>
              </Box>

              {/* JurisConsult Data if available */}
              {precatorio.qualidade && (
                <Box bg={bg} p={4} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                  <Text fontSize="lg" fontWeight="bold" mb={3}>
                    Dados JurisConsult
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <InfoRow label="Qualidade" value={precatorio.qualidade} />
                    {precatorio.jurisConsultData && (
                      <>
                        <InfoRow label="Advogado" value={precatorio.jurisConsultData.advogado} />
                        <InfoRow label="OAB" value={precatorio.jurisConsultData.oab} />
                      </>
                    )}
                  </SimpleGrid>
                </Box>
              )}

              {/* Status History */}
              {precatorio.statusUpdatedBy && (
                <Box bg={bg} p={4} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                  <Text fontSize="lg" fontWeight="bold" mb={3}>
                    Última Atualização
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <InfoRow label="Atualizado por" value={precatorio.statusUpdatedBy.userName} />
                    <InfoRow label="Data" value={formatDate(precatorio.statusUpdatedBy.updatedAt)} />
                  </SimpleGrid>
                </Box>
              )}

              {/* Timestamps */}
              <Box bg={bg} p={4} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                <Text fontSize="lg" fontWeight="bold" mb={3}>
                  Datas do Sistema
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <InfoRow label="Recebimento" value={precatorio.recebimento} />
                  <InfoRow label="Criado em" value={formatDate(precatorio.createdAt)} />
                  <InfoRow label="Atualizado em" value={formatDate(precatorio.updatedAt)} />
                </SimpleGrid>
              </Box>
            </VStack>
          ) : (
            <Text>Nenhum precatório selecionado</Text>
          )}
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="blue" onClick={onClose}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PrecatoriosSelecionadasModal;